import React, { useState, useEffect, useRef } from 'react';
import { MdViewModule, MdExpandMore, MdExpandLess } from 'react-icons/md';

const EntityForm = ({ erdState, currentEntity, setCurrentEntity, editingMode, setEditingMode, onSaveEvent }) => {
  const [expandedAttributes, setExpandedAttributes] = useState({});
  const [pendingSaveEvent, setPendingSaveEvent] = useState(null);
  const inputTextRef = useRef(erdState.inputText);

  // Toggle individual attribute expansion
  const toggleAttributeExpansion = (index) => {
    setExpandedAttributes(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Add attribute input field
  const addEntityAttribute = () => {
    setCurrentEntity(prev => ({
      ...prev,
      attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }, ...prev.attributes]
    }));

    // Shift all existing expansion indicies up by 1
    setExpandedAttributes(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        const numKey = parseInt(key);
        newState[numKey + 1] = prev[numKey]
      });
      // Auto-expanded new attribute at index 0
      newState[0] = true;
      return newState;
    });
  };

  // Remove attribute field
  const removeEntityAttribute = (index) => {
    setCurrentEntity(prev => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index)
    }));
    // Clean up expansion state
    setExpandedAttributes(prev => {
      const newState = { ...prev };
      delete newState[index];
      // Adjust indices for remaining attributes
      Object.keys(newState).forEach(key => {
        const numKey = parseInt(key);
        if (numKey > index) {
          newState[numKey - 1] = newState[numKey];
          delete newState[numKey];
        }
      });
      return newState;
    });
  };

  // Update entity attribute
  const updateEntityAttribute = (index, field, value) => {
    setCurrentEntity(prev => ({
      ...prev,
      attributes: prev.attributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr
      )
    }));
  };

  // Generate or update entity notation
  const generateEntityNotation = () => {
    if (!currentEntity.name.trim()) {
      alert('Please enter an entity name');
      return;
    }

    const entityTypes = currentEntity.isWeak ? ' @weak' : '';
    let entityNotation = `[Entity]\n${currentEntity.name}${entityTypes}\n--`;

    const validAttributes = currentEntity.attributes.filter(attr => attr.name.trim() !== '');
    if (validAttributes.length === 0) {
      entityNotation += '\n';
    } else {
      entityNotation += '\n';
      validAttributes.forEach(attr => {
        let attrLine = attr.name;

        // Add sub-attributes if specified
        if (attr.subAttributes.trim()) {
          attrLine += ` (${attr.subAttributes})`;
        }

        // Add attribute tags
        const tags = [];
        if (attr.isKey) tags.push('@key');
        if (attr.isPartialKey) tags.push('@partial_key');
        if (attr.isMultivalued) tags.push('@multivalued');
        if (attr.isDerived) tags.push('@derived');

        if (tags.length > 0) {
          attrLine += ' ' + tags.join(' ');
        }

        entityNotation += `${attrLine}\n`;
      });
    }

    // Check if we're editing an existing component
    if (editingMode && editingMode.isEditing && editingMode.componentType === 'entity' && editingMode.originalComponent) {
      const originalName = editingMode.originalComponent.name;

      // Split notation into lines for precise replacement
      const lines = erdState.inputText.split('\n');
      const newLines = [];
      let i = 0;
      let replaced = false;

      while (i < lines.length) {
        const line = lines[i];

        // Look for [Entity] followed by the original entity name
        if (line.trim() === '[Entity]' && i + 1 < lines.length) {
          const nextLine = lines[i + 1].trim();
          const nameMatch = nextLine.match(/^(\w+)/);

          if (nameMatch && nameMatch[1] === originalName) {
            // Found the entity to replace - add the new notation
            newLines.push(...entityNotation.split('\n'));
            replaced = true;

            // Skip all lines until we hit the next [Section] or end of file
            i += 2; // Skip [Entity] and name line
            while (i < lines.length && !lines[i].trim().startsWith('[')) {
              i++;
            }
            continue; // Don't increment i again
          }
        }

        newLines.push(line);
        i++;
      }

      if (replaced) {
        let finalNotation = newLines.join('\n');

        // Update relationship and specialization references
        if (originalName !== currentEntity.name) {

          // Update relationships that reference the old entity name (pattern: "EntityName (cardinality)")
          finalNotation = finalNotation.replace(
            new RegExp(`\\b${originalName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b(?=\\s*\\()`, 'g'),
            currentEntity.name
          );

          // Update specializations that reference the old entity name (pattern: "EntityName @disjoint")
          finalNotation = finalNotation.replace(
            new RegExp(`\\b${originalName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b(?=\\s*@)`, 'g'),
            currentEntity.name
          );

        }

        erdState.setInputText(finalNotation);
        // Set pending save event for after state update
        setPendingSaveEvent('entity_form_submitted');
      } else {
        erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + entityNotation);
        // Set pending save event for after state update
        setPendingSaveEvent('entity_form_submitted');
      }

      // Clear editing mode
      setEditingMode({
        isEditing: false,
        componentType: null,
        originalComponent: null
      });
    } else {
      // Add new component
      erdState.setInputText(prev => prev + (prev ? '\n\n' : '') + entityNotation);
      // Set pending save event for after state update
      setPendingSaveEvent('entity_added');
    }

    // Reset form after successful addition/edit
    setCurrentEntity({
      name: '',
      isWeak: false,
      attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
    });
  };

  // Effect to track inputText changes and trigger pending save events
  useEffect(() => {
    // Only trigger save if inputText actually changed and we have a pending event
    if (erdState.inputText !== inputTextRef.current && pendingSaveEvent && onSaveEvent) {
      inputTextRef.current = erdState.inputText;
      // Use setTimeout to ensure the state has fully propagated
      setTimeout(() => {
        onSaveEvent(pendingSaveEvent);
        setPendingSaveEvent(null);
      }, 0);
    } else {
      inputTextRef.current = erdState.inputText;
    }
  }, [erdState.inputText, pendingSaveEvent, onSaveEvent]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          {editingMode && editingMode.isEditing && editingMode.componentType === 'entity'
            ? `Edit Entity: ${editingMode.originalComponent?.name || 'Unknown'}`
            : 'Add Entity'}
        </h2>
        <MdViewModule className="w-6 h-6 text-gray-600" />
      </div>

      {/* Entity Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Entity Name</label>
        <input
          type="text"
          placeholder="e.g., Employee, Customer"
          value={currentEntity.name}
          onChange={(e) => setCurrentEntity(prev => ({ ...prev, name: e.target.value }))}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Entity Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Entity Type</label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={currentEntity.isWeak}
            onChange={(e) => setCurrentEntity(prev => ({ ...prev, isWeak: e.target.checked }))}
            className="mr-2 rounded"
          />
          <span className="text-sm">Weak Entity</span>
        </label>
      </div>

      {/* Attributes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Attributes ({currentEntity.attributes.filter(attr => attr.name.trim() !== '').length})
          </label>
          <button
            onClick={addEntityAttribute}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            + Add Attribute
          </button>
        </div>

        <div className="space-y-3">
          {currentEntity.attributes.map((attr, index) => {
            const isExpanded = expandedAttributes[index] !== false; // Default to expanded
            const hasContent = attr.name.trim() !== '' || attr.subAttributes.trim() !== '' ||
              attr.isKey || attr.isPartialKey || attr.isMultivalued || attr.isDerived;

            return (
              <div key={index} className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden transition-all duration-200">
                {/* Attribute Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => toggleAttributeExpansion(index)}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-700">
                      {attr.name.trim() !== '' ? attr.name : `Attribute ${index + 1}`}
                    </span>
                    {hasContent && (
                      <div className="ml-2 flex gap-1">
                        {attr.isKey && <span className="text-xs bg-green-100 text-green-700 px-1 rounded">Key</span>}
                        {attr.isPartialKey && <span className="text-xs bg-red-100 text-red-700 px-1 rounded">Partial</span>}
                        {attr.isMultivalued && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">Multi</span>}
                        {attr.isDerived && <span className="text-xs bg-yellow-100 text-yellow-700 px-1 rounded">Derived</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {currentEntity.attributes.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEntityAttribute(index);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs px-2 py-1 transition-colors duration-150"
                      >
                        Remove
                      </button>
                    )}
                    <div className="transition-transform duration-200">
                      {isExpanded ? (
                        <MdExpandLess className="w-4 h-4 text-gray-500" />
                      ) : (
                        <MdExpandMore className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Attribute Content */}
                <div className={`transition-all duration-200 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    {/* Attribute Name */}
                    <input
                      type="text"
                      placeholder="Attribute name (e.g., FirstName, ID)"
                      value={attr.name}
                      onChange={(e) => updateEntityAttribute(index, 'name', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    />

                    {/* Sub-attributes */}
                    <input
                      type="text"
                      placeholder="Sub-attributes (e.g., street,city,state)"
                      value={attr.subAttributes}
                      onChange={(e) => updateEntityAttribute(index, 'subAttributes', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 transition-all duration-150"
                    />

                    {/* Attribute Types */}
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={attr.isKey}
                          onChange={(e) => updateEntityAttribute(index, 'isKey', e.target.checked)}
                          className="mr-2 rounded transition-all duration-150"
                        />
                        Key
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={attr.isPartialKey}
                          onChange={(e) => updateEntityAttribute(index, 'isPartialKey', e.target.checked)}
                          className="mr-2 rounded transition-all duration-150"
                        />
                        Partial Key
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={attr.isMultivalued}
                          onChange={(e) => updateEntityAttribute(index, 'isMultivalued', e.target.checked)}
                          className="mr-2 rounded transition-all duration-150"
                        />
                        Multivalued
                      </label>
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={attr.isDerived}
                          onChange={(e) => updateEntityAttribute(index, 'isDerived', e.target.checked)}
                          className="mr-2 rounded transition-all duration-150"
                        />
                        Derived
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Entity Button */}
      <button
        onClick={generateEntityNotation}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium transition-colors duration-200"
      >
        {editingMode.isEditing && editingMode.componentType === 'entity'
          ? 'Confirm Edits'
          : 'Add Entity to Notation'}
      </button>
    </div>
  );
};

export default EntityForm;